/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import {
	MentionedByLinkedResults,
	SimilarLinkedResults,
} from '../../../../../../../common/ui/linked-results';
import { processExcerpt } from '../../../../../../../common/utils/search-result-utils';

import { convertVideoDuration } from './convert-video-duration';
import { isLoomUrlExpired } from './is-loom-url-expired';
import { BaseVideoSearchResult } from './shared/base-video-search-result';
import { isChapter } from './shared/is-chapter';
import { type ExternalChapter, type VideoResultRendererProps } from './shared/types';
import { VideoChapters } from './shared/video-chapters';
import { VideoThumbnail } from './shared/video-thumbnail';

const videoresultsStyles = cssMap({
	bodyAreaStyles: {
		display: 'flex',
		columnGap: token('space.150'),
	},
});

/**
 * Renderer for regular video search results (e.g., Loom videos).
 * Shows thumbnail, video duration, and chapter navigation.
 */
export const VideoResultRenderer: (props: VideoResultRendererProps) => JSX.Element = (
	props: VideoResultRendererProps,
) => {
	const {
		data,
		formatMessage,
		onResultClicked,
		onResultContextMenu,
		position,
		selectedProduct,
		product,
		is3pGroupingEnabled,
		thirdPartyConfigs,
		linkedResults,
	} = props;

	const { url, entity, integrationId } = data;
	const { durationInSeconds, chapters, thumbnailUrl } = entity || {};
	const { excerpt } = data;

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true, formatMessage });

	const body = (
		<Box testId="thumbnail-container" xcss={videoresultsStyles.bodyAreaStyles}>
			<VideoThumbnail thumbnailUrl={thumbnailUrl} url={url} />
			{thumbnailUrl && !isLoomUrlExpired(thumbnailUrl) ? (
				<Box>{processExcerpt(excerpt, true)} </Box>
			) : (
				<DescriptionBody description={processExcerpt(excerpt, true)} />
			)}
		</Box>
	);

	const isLoom = integrationId === 'ari:cloud:platform::integration/loom';
	const shouldRenderSimilarResults =
		isLoom &&
		linkedResults?.some((linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR');
	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		shouldRenderSimilarResults ? (
			<SimilarLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClicked}
				isAllProductSelected={!selectedProduct}
				onResultContextMenu={onResultContextMenu}
				is3pGroupingEnabled={is3pGroupingEnabled}
				product={product}
				fallbackType="video"
			/>
		) : (
			<MentionedByLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				isAllProductSelected={!selectedProduct}
			/>
		)
	) : (
		<Box>
			<VideoChapters chapters={(chapters ?? []).filter(isChapter) as ExternalChapter[]} url={url} />
		</Box>
	);

	return (
		<BaseVideoSearchResult
			{...props}
			body={body}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			additionalMetadataParts={[['video-duration', videoDuration]]}
		/>
	);
};
